import { useContext } from 'react';
import Scrollbar from 'src/components/Scrollbar';
import { SidebarContext } from 'src/contexts/SidebarContext';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Drawer,
  styled,
  Divider,
  useTheme,
  Tooltip,
  IconButton
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import SidebarMenu from './SidebarMenu';

const SidebarWrapper = styled(Box)(
  ({ theme }) => `
        width: ${theme.sidebar.width};
        min-width: ${theme.sidebar.width};
        color: ${theme.sidebar.ink[70]};
        z-index: 7;
        height: 100%;
`
);

function Sidebar() {
  const { sidebarToggle, toggleSidebar, desktopSidebarOpen, toggleDesktopSidebar } =
    useContext(SidebarContext);
  const closeSidebar = () => toggleSidebar();
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  // Check if current language is Arabic/RTL
  const isRTL = i18n.language === 'ar' || theme.direction === 'rtl';

  return (
    <>
      <SidebarWrapper
        sx={{
          display: {
            xs: 'none',
            lg: 'inline-block'
          },
          position: 'fixed',
          top: 80,
          height: 'calc(100% - 80px)',
          // Position sidebar on right for RTL, left for LTR
          ...(isRTL ? { right: 0 } : { left: 0 }),
          // Slide the sidebar off-screen when closed on desktop
          transform: desktopSidebarOpen
            ? 'translateX(0)'
            : `translateX(${isRTL ? '100%' : '-100%'})`,
          visibility: desktopSidebarOpen ? 'visible' : 'hidden',
          transition: theme.transitions.create(['transform', 'visibility'], {
            duration: theme.transitions.duration.standard
          }),
          background: theme.sidebar.surface,
          boxShadow: theme.sidebar.boxShadow
        }}
      >
        <Scrollbar
          thumbColor="rgba(24, 230, 142, 0.5)"
          thumbHoverColor="#0ab76c"
        >
          <Divider
            sx={{
              mt: theme.spacing(3),
              mx: theme.spacing(2),
              background: theme.sidebar.ink[10]
            }}
          />
          <SidebarMenu />
        </Scrollbar>
      </SidebarWrapper>
      <Box
        sx={{
          display: {
            xs: 'none',
            lg: 'flex'
          },
          position: 'fixed',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 8,
          transition: theme.transitions.create(['left', 'right'], {
            duration: theme.transitions.duration.standard
          }),
          // Docked on the sidebar edge when open, flush to the screen edge when closed
          ...(isRTL
            ? {
                right: desktopSidebarOpen
                  ? `calc(${theme.sidebar.width} - 18px)`
                  : 8
              }
            : {
                left: desktopSidebarOpen
                  ? `calc(${theme.sidebar.width} - 18px)`
                  : 8
              })
        }}
      >
        <Tooltip arrow title={t('sidebar.toggle_menu')}>
          <IconButton
            size="small"
            onClick={toggleDesktopSidebar}
            sx={{
              background: '#18E68E',
              border: '1px solid #18E68E',
              boxShadow: theme.sidebar.boxShadow,
              color: '#000',
              '&:hover': {
                background: '#18E68E'
              }
            }}
          >
            {desktopSidebarOpen === !isRTL ? (
              <ChevronLeftIcon fontSize="small" sx={{ color: '#000' }} />
            ) : (
              <ChevronRightIcon fontSize="small" sx={{ color: '#000' }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      <Drawer
        sx={{
          boxShadow: `${theme.sidebar.boxShadow}`
        }}
        // Drawer opens from right in RTL, left in LTR
        anchor={isRTL ? 'right' : 'left'}
        open={sidebarToggle}
        onClose={closeSidebar}
        variant="temporary"
        elevation={9}
      >
        <SidebarWrapper
          sx={{
            background:
              theme.palette.mode === 'dark'
                ? theme.colors.alpha.white[100]
                : theme.sidebar.surface
          }}
        >
          <Scrollbar
            thumbColor="rgba(24, 230, 142, 0.5)"
            thumbHoverColor="#0ab76c"
          >
            <Box mt={3}></Box>
            <Divider
              sx={{
                mt: theme.spacing(3),
                mx: theme.spacing(2),
                background: theme.sidebar.ink[10]
              }}
            />
            <SidebarMenu />
          </Scrollbar>
        </SidebarWrapper>
      </Drawer>
    </>
  );
}

export default Sidebar;
